// Converts physical paint state into lighting inputs, per dirty tile:
//   albedo   linear-RGB diffuse reflectance from spectral Kubelka-Munk
//            layering (dried stack -> settled pigment -> suspended pigment ->
//            wet body), with the Saunderson surface correction. No lighting.
//   height   surface elevation (paper relief + dried film + wet body + water)
//   gloss    surface smoothness (wet water/oil is glossy, dry paper is matte)
//   wetness  how wet the surface reads (for display/debug views)
//
// Wet darkening: water filling the gaps between paper fibres and pigment
// particles reduces the refractive-index contrast that causes scattering, so
// wet paint looks darker and more saturated. We approximate the loss of
// scattering by raising the internal reflectance to a power (1 + 0.45 * wet),
// which deepens colour without changing hue. This is an approximation.

import { BAND_COUNT, BAND_TO_LINEAR_RGB } from '../pigments/ColourConversion.js';
import { kmLayerReflectance, saundersonBody } from '../pigments/SpectralMixing.js';
import { PIGMENT_SLOTS, TILE_SIZE } from '../core/PaintState.js';
import { DEFAULT_PAPER_PARAMS } from '../physics/PaperAbsorption.js';

export const RELIEF = Object.freeze({ paper: 0.9, dried: 6.0, body: 6.0, water: 1.5 });

export function createShadingBuffers(width, height) {
  const n = width * height;
  return {
    width,
    height,
    albedo: new Float32Array(n * 3),
    height_: null,
    elevation: new Float32Array(n),
    gloss: new Float32Array(n),
    wetness: new Float32Array(n),
    srgb8: new Uint8ClampedArray(n * 4),
    material8: new Uint8ClampedArray(n * 4),
  };
}

const spectrum = new Float64Array(BAND_COUNT);

function compositeFields(state, i, fields) {
  let present = false;
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    if (state.slots[k] && fields[k][i] > 1e-7) {
      present = true;
      break;
    }
  }
  if (!present) return;
  for (let band = 0; band < BAND_COUNT; band += 1) {
    let KX = 0;
    let SX = 0;
    for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
      const pigment = state.slots[k];
      if (!pigment) continue;
      const a = fields[k][i];
      if (a <= 1e-7) continue;
      KX += a * pigment.K[band];
      SX += a * pigment.S[band];
    }
    spectrum[band] = kmLayerReflectance(KX, SX, spectrum[band]);
  }
}

function linearToSrgb8(v) {
  const c = v <= 0 ? 0 : v >= 1 ? 1 : v;
  return Math.round((c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055) * 255);
}

export function shadeCell(state, buffers, i) {
  const base = i * BAND_COUNT;
  for (let band = 0; band < BAND_COUNT; band += 1) spectrum[band] = state.dryRefl[base + band];
  compositeFields(state, i, state.depPig);
  compositeFields(state, i, state.wetPig);
  const hasBody = state.bodyVol[i] > 1e-4;
  if (hasBody) compositeFields(state, i, state.bodyPig);
  const h = state.water[i];
  const wet = Math.min(1, state.paperSat[i] / DEFAULT_PAPER_PARAMS.capacity + h * 6);
  const exponent = 1 + 0.45 * wet;
  const m = BAND_TO_LINEAR_RGB;
  let r = 0;
  let g = 0;
  let b = 0;
  for (let band = 0; band < BAND_COUNT; band += 1) {
    let R = spectrum[band];
    if (wet > 0) R = Math.pow(R, exponent);
    const body = saundersonBody(R);
    r += m[0][band] * body;
    g += m[1][band] * body;
    b += m[2][band] * body;
  }
  buffers.albedo[i * 3] = r;
  buffers.albedo[i * 3 + 1] = g;
  buffers.albedo[i * 3 + 2] = b;
  const bodyRelief = hasBody ? state.bodyVol[i] : 0;
  buffers.elevation[i] = RELIEF.paper * state.surface.heightField[i] + RELIEF.dried * state.dryHeight[i] + RELIEF.body * bodyRelief + RELIEF.water * h;
  let gloss = 0.06;
  if (state.dryHeight[i] > 0.01) gloss = 0.35;
  if (hasBody) gloss = Math.max(gloss, state.bodyGloss[i] * (0.4 + 0.6 * state.bodyOpen[i]));
  if (h > 0.004) gloss = Math.max(gloss, Math.min(0.92, 0.5 + h * 8));
  buffers.gloss[i] = gloss;
  buffers.wetness[i] = Math.max(wet, hasBody ? state.bodyOpen[i] : 0);
  const o = i * 4;
  buffers.srgb8[o] = linearToSrgb8(r);
  buffers.srgb8[o + 1] = linearToSrgb8(g);
  buffers.srgb8[o + 2] = linearToSrgb8(b);
  buffers.srgb8[o + 3] = 255;
  buffers.material8[o] = Math.round(gloss * 255);
  buffers.material8[o + 1] = Math.round(buffers.wetness[i] * 255);
  buffers.material8[o + 2] = 0;
  buffers.material8[o + 3] = 255;
}

// Re-shades dirty tiles; returns the list of tiles that changed (for GPU upload).
export function shadeDirtyTiles(state, buffers, maxTiles = Infinity) {
  const changed = [];
  const { width, height, tilesX } = state;
  for (let t = 0; t < state.dirtyTiles.length && changed.length < maxTiles; t += 1) {
    if (!state.dirtyTiles[t]) continue;
    state.dirtyTiles[t] = 0;
    const tx = t % tilesX;
    const ty = (t - tx) / tilesX;
    const x0 = tx * TILE_SIZE;
    const y0 = ty * TILE_SIZE;
    const x1 = Math.min(width, x0 + TILE_SIZE);
    const y1 = Math.min(height, y0 + TILE_SIZE);
    for (let y = y0; y < y1; y += 1) for (let x = x0; x < x1; x += 1) shadeCell(state, buffers, y * width + x);
    changed.push(t);
  }
  return changed;
}

export function shadeAll(state, buffers) {
  state.dirtyTiles.fill(1);
  return shadeDirtyTiles(state, buffers);
}
